import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useInView } from 'framer-motion';
import {
  Database, MessageSquare, Layers, CheckCircle, ArrowRight,
  Sparkles, Zap, Shield, Settings, Globe2, Users, BarChart2
} from 'lucide-react';
import SiteNavbar from '../components/SiteNavbar';
import AIAssistant from '../components/AIAssistant';

function Section({ children, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.section>
  );
}

function Badge({ children, variant = 'primary' }) {
  const variants = {
    primary: 'bg-primary/10 text-primary border border-primary/20',
    accent:  'bg-accent/15 text-amber-700 border border-accent/30',
    dark:    'bg-white/10 text-white/80 border border-white/20',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase ${variants[variant]}`}>
      {children}
    </span>
  );
}

export default function SMESolutions() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const lang = i18n.language;

  const services = [
    {
      icon: Database,
      titleKey: 'erp',
      descKey: 'erpDesc',
      color: 'text-primary',
      bg: 'bg-primary/8',
      border: 'border-primary/20',
      features: lang === 'ar'
        ? ['تطبيق Odoo المُخصَّص لنموذج عملك', 'إعداد وتهيئة الوحدات', 'ترحيل البيانات', 'التدريب والدعم بعد الإطلاق']
        : ['Odoo implementation tailored to your business', 'Module configuration and setup', 'Data migration', 'Training and post-go-live support'],
    },
    {
      icon: MessageSquare,
      titleKey: 'crm',
      descKey: 'crmDesc',
      color: 'text-success',
      bg: 'bg-emerald-50',
      border: 'border-success/20',
      features: lang === 'ar'
        ? ['أتمتة محادثات العملاء عبر WhatsApp', 'تتبع العملاء المحتملين والمتابعات', 'تكامل مع CRM الحالي', 'لوحة تحكم التحليلات']
        : ['Automate customer conversations on WhatsApp', 'Lead tracking and follow-up pipelines', 'Integration with your existing CRM', 'Analytics dashboard'],
    },
    {
      icon: Layers,
      titleKey: 'workflow',
      descKey: 'workflowDesc',
      color: 'text-accent',
      bg: 'bg-accent/8',
      border: 'border-accent/25',
      features: lang === 'ar'
        ? ['رسم خرائط العمليات الحالية', 'تصميم سير عمل رقمي', 'أتمتة الموافقات والإشعارات', 'تتبع الأداء والتقارير']
        : ['Map your current manual processes', 'Design digital workflow replacements', 'Automate approvals and notifications', 'Performance tracking and reporting'],
    },
  ];

  const stats = lang === 'ar'
    ? [{ val: '60%', label: 'تقليل في الوقت التشغيلي' }, { val: '3×', label: 'سرعة في الاستجابة للعملاء' }, { val: '100%', label: 'رقمنة العمليات' }]
    : [{ val: '60%', label: 'Reduction in manual ops time' }, { val: '3×', label: 'Faster customer response' }, { val: '100%', label: 'Process digitization' }];

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'font-arabic' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <SiteNavbar />
      <AIAssistant source="sme" />

      {/* HERO */}
      <section className="relative bg-midnight -mt-16 pt-16 min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 blueprint-grid-dark opacity-50" />
        <div className="absolute top-1/4 left-1/3 w-80 h-80 rounded-full bg-accent/10 blur-[100px]" />
        <div className="absolute bottom-0 right-1/4 w-60 h-60 rounded-full bg-primary/12 blur-[80px]" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Badge variant="accent"><Sparkles size={12} />{t('sme.badge')}</Badge>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 text-4xl sm:text-5xl font-black text-white leading-tight"
            >
              {t('sme.headline')}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-4 text-white/60 text-base leading-relaxed"
            >
              {t('sme.subheadline')}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <button
                onClick={() => navigate('/contact')}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-accent text-[#1A1A1A] font-semibold text-sm hover:bg-accent-hover transition-all"
              >
                {t('nav.bookDemo')}
                <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
              </button>
              <button
                onClick={() => navigate('/contact')}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white/8 text-white font-semibold text-sm hover:bg-white/14 transition-all border border-white/20"
              >
                {t('cta.contactUs')}
              </button>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55 }}
              className="mt-12 flex flex-wrap gap-10"
            >
              {stats.map((s, i) => (
                <div key={i}>
                  <p className="text-3xl font-black gradient-text-accent">{s.val}</p>
                  <p className="text-white/45 text-xs mt-0.5">{s.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <Section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge variant="primary"><Layers size={12} />{lang === 'ar' ? 'خدماتنا' : 'Our Services'}</Badge>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-heading">
              {lang === 'ar' ? 'ثلاثة حلول. قيمة واحدة.' : 'Three Services. One Goal.'}
            </h2>
            <p className="mt-3 text-body text-sm max-w-xl mx-auto">
              {lang === 'ar' ? 'نساعد الشركات الصغيرة والمتوسطة على بناء أساس رقمي قوي يدعم نموها.' : 'We help SMEs build a strong digital foundation that supports their growth.'}
            </p>
          </div>

          <div className="space-y-5">

            {/* ── ERP: large horizontal card ── */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl overflow-hidden shadow-sm border border-slate-100 grid grid-cols-1 lg:grid-cols-2"
            >
              {/* Content */}
              <div className={`p-6 sm:p-8 lg:p-12 flex flex-col justify-center ${isRTL ? 'lg:order-2' : ''}`}>
                <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-600 border border-blue-100 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest w-fit mb-5">
                  <Database size={11} />
                  {lang === 'ar' ? 'نظام ERP' : 'ERP System'}
                </span>
                <h3 className="text-3xl sm:text-4xl font-black text-slate-900 leading-tight">
                  {lang === 'ar' ? 'تطبيق نظام ERP' : t('sme.erp')}
                </h3>
                <p className="mt-4 text-slate-500 text-sm leading-relaxed max-w-md">
                  {t('sme.erpDesc')}
                </p>
                <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {services[0].features.map((f, j) => (
                    <div key={j} className="flex items-start gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle size={11} className="text-blue-500" />
                      </div>
                      <span className="text-slate-700 text-sm leading-snug">{f}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-8">
                  <button
                    onClick={() => navigate('/contact')}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-all shadow-sm hover:shadow-primary-glow"
                  >
                    {lang === 'ar' ? 'احجز عرضاً' : 'Book a Demo'}
                    <ArrowRight size={15} className={isRTL ? 'rotate-180' : ''} />
                  </button>
                </div>
              </div>

              {/* Image panel */}
              <div className={`relative bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 flex items-end justify-center overflow-hidden min-h-[320px] ${isRTL ? 'lg:order-1' : ''}`}>
                <div className="absolute top-6 right-6 w-24 h-24 rounded-full bg-blue-200/40 blur-2xl" />
                <div className="absolute bottom-10 left-6 w-16 h-16 rounded-full bg-indigo-300/30 blur-xl" />
                <motion.img
                  src="/sme/ERP.png"
                  alt="ERP System"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="relative z-10 w-full max-w-sm object-contain drop-shadow-2xl px-6 pb-0 pt-8"
                />
              </div>
            </motion.div>

            {/* ── WA CRM + Workflow: two cards ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

              {/* WhatsApp CRM */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-3xl overflow-hidden shadow-sm border border-slate-100 flex flex-col"
              >
                <div className="relative bg-gradient-to-br from-emerald-50 via-green-50 to-teal-100 flex items-end justify-center overflow-hidden h-56">
                  <div className="absolute top-4 right-4 w-20 h-20 rounded-full bg-emerald-200/50 blur-2xl" />
                  <motion.img
                    src="/sme/WA CRM.png"
                    alt="WhatsApp CRM"
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                    className="relative z-10 h-full w-auto max-w-full object-contain drop-shadow-xl px-6 pt-6"
                  />
                </div>
                <div className="p-8 flex flex-col flex-1">
                  <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest w-fit mb-4">
                    <MessageSquare size={11} />
                    {lang === 'ar' ? 'إدارة العملاء' : 'CRM'}
                  </span>
                  <h3 className="text-2xl font-black text-slate-900 leading-tight">
                    {t('sme.crm')}
                  </h3>
                  <p className="mt-3 text-slate-500 text-sm leading-relaxed">
                    {t('sme.crmDesc')}
                  </p>
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {services[1].features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2.5">
                        <div className="w-5 h-5 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle size={11} className="text-emerald-500" />
                        </div>
                        <span className="text-slate-700 text-sm leading-snug">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => navigate('/contact')}
                    className="mt-7 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-emerald-600 text-emerald-700 font-semibold text-sm hover:bg-emerald-600 hover:text-white transition-all duration-200 w-fit"
                  >
                    {lang === 'ar' ? 'اعرف أكثر' : 'Learn More'}
                    <ArrowRight size={14} className={isRTL ? 'rotate-180' : ''} />
                  </button>
                </div>
              </motion.div>

              {/* Workflow Automation */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-3xl overflow-hidden shadow-sm border border-slate-100 flex flex-col"
              >
                <div className="relative bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-100 flex items-end justify-center overflow-hidden h-56">
                  <div className="absolute top-4 left-4 w-20 h-20 rounded-full bg-amber-200/50 blur-2xl" />
                  <motion.img
                    src="/sme/Workflow.png"
                    alt="Workflow Automation"
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.25 }}
                    className="relative z-10 h-full w-auto max-w-full object-contain drop-shadow-xl px-6 pt-6"
                  />
                </div>
                <div className="p-8 flex flex-col flex-1">
                  <span className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 border border-amber-100 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest w-fit mb-4">
                    <Layers size={11} />
                    {lang === 'ar' ? 'أتمتة' : 'Automation'}
                  </span>
                  <h3 className="text-2xl font-black text-slate-900 leading-tight">
                    {t('sme.workflow')}
                  </h3>
                  <p className="mt-3 text-slate-500 text-sm leading-relaxed">
                    {t('sme.workflowDesc')}
                  </p>
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {services[2].features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2.5">
                        <div className="w-5 h-5 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle size={11} className="text-amber-500" />
                        </div>
                        <span className="text-slate-700 text-sm leading-snug">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => navigate('/contact')}
                    className="mt-7 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-amber-500 text-amber-700 font-semibold text-sm hover:bg-amber-500 hover:text-white transition-all duration-200 w-fit"
                  >
                    {lang === 'ar' ? 'اعرف أكثر' : 'Learn More'}
                    <ArrowRight size={14} className={isRTL ? 'rotate-180' : ''} />
                  </button>
                </div>
              </motion.div>

            </div>
          </div>
        </div>
      </Section>

      {/* PROCESS */}
      <Section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge variant="primary"><Zap size={12} />{lang === 'ar' ? 'منهجيتنا' : 'Our Approach'}</Badge>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-heading">
              {lang === 'ar' ? 'من الفكرة إلى التشغيل' : 'From Idea to Live System'}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {(lang === 'ar'
              ? ['اكتشاف العمليات', 'التصميم والإعداد', 'التطبيق والاختبار', 'الإطلاق والدعم']
              : ['Process Discovery', 'Design & Configuration', 'Implementation & Testing', 'Go-Live & Support']
            ).map((phase, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-border-light text-center card-hover relative"
              >
                <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-black text-lg mx-auto mb-4">
                  {i + 1}
                </div>
                <h3 className="text-heading font-bold text-sm">{phase}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* WHY TULY */}
      <Section className="py-20 bg-midnight relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid-dark opacity-40" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[300px] bg-accent/8 blur-[100px] rounded-full" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="dark"><Shield size={12} />{lang === 'ar' ? 'لماذا Tuly Technology' : 'Why Tuly Technology'}</Badge>
              <h2 className="mt-4 text-3xl sm:text-4xl font-black text-white">
                {lang === 'ar' ? 'شريك تقني. ليس مجرد مزود.' : 'A Technology Partner. Not Just a Vendor.'}
              </h2>
              <p className="mt-3 text-white/60 text-sm leading-relaxed">
                {lang === 'ar'
                  ? 'نفهم تحديات الشركات الصغيرة والمتوسطة في المنطقة. نبني حلولاً تعمل فعلاً في السياق المحلي.'
                  : 'We understand the challenges of SMEs in the region. We build solutions that actually work in the local context.'}
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4">
                {[
                  { icon: Globe2,    label: lang === 'ar' ? 'دعم عربي وإنجليزي' : 'Arabic & English Support' },
                  { icon: Shield,    label: lang === 'ar' ? 'تكامل آمن' : 'Secure Integration' },
                  { icon: Users,     label: lang === 'ar' ? 'تدريب الفريق' : 'Team Training' },
                  { icon: BarChart2, label: lang === 'ar' ? 'قياس النتائج' : 'Results Measurement' },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-3 p-3 rounded-xl bg-deep-surface border border-dark-border"
                  >
                    <item.icon size={16} className="text-accent flex-shrink-0" />
                    <span className="text-white/80 text-xs font-medium">{item.label}</span>
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden">
                <img src="/industrial/engineering drawings 5.jpg" alt="SME" className="w-full object-cover opacity-70" />
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto text-center px-4">
          <h2 className="text-3xl sm:text-4xl font-black text-heading">
            {lang === 'ar' ? 'هل أنت مستعد لرقمنة عملياتك؟' : 'Ready to Digitize Your Operations?'}
          </h2>
          <p className="mt-3 text-body text-sm leading-relaxed">
            {lang === 'ar' ? 'تواصل معنا لمناقشة احتياجاتك.' : "Let's talk about your specific needs."}
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => navigate('/contact')}
              className="flex items-center gap-2 px-7 py-3.5 rounded-xl bg-primary text-white font-semibold hover:bg-primary-hover transition-all shadow-primary-glow"
            >
              {t('nav.bookDemo')}
              <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
            </button>
          </div>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="bg-midnight border-t border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/tuly.png" alt="Tuly Technology" className="h-7 w-auto" />
              <span className="text-white font-bold text-sm">Tuly Technology</span>
            </div>
            <p className="text-muted text-xs">© {new Date().getFullYear()} Tuly Technology. {t('footer.allRights')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
